/**
 * 框选几何计算 · 纯函数，无 React、无副作用，供 useBoxSelect 使用。
 *
 * 全部围绕"内容坐标系"——相对滚动内容顶部 / 左侧的坐标，不随滚动位置变化。
 * 这样框选过程中容器自动滚动时，起点锚点和各项位置都不用跟着重算，命中检测只做
 * 算术、不再逐帧读布局。
 */

/** 单个可选项在内容坐标系下的矩形 · 框选开始时快照一次，滚动中不变 */
export interface ItemContentRect {
  /** 可选项 id（取自标识属性，如 data-folder-id 的值） */
  id: string
  left: number
  right: number
  top: number
  bottom: number
}

/**
 * 向上找最近的可滚动祖先 · 作为框选的可视视口与自动滚动对象。
 *
 * 不能直接拿框选容器自身当视口：容器常用 min-height:100% 撑满滚动父级，内容多时
 * 比视口高，自身 getBoundingClientRect 会落到屏幕外。
 *
 * @param container - 框选作用容器
 * @returns 最近的可滚动祖先；找不到（容器本身即视口）则返回容器自身
 *
 * @example
 * ```ts
 * const scroller = resolveScrollContainer(containerEl) // → .folderArea
 * ```
 */
export function resolveScrollContainer(container: HTMLElement): HTMLElement {
  const SCROLLABLE = /(auto|scroll|overlay)/
  let node: HTMLElement | null = container.parentElement
  while (node) {
    const { overflowX, overflowY } = window.getComputedStyle(node)
    if (SCROLLABLE.test(overflowY) || SCROLLABLE.test(overflowX)) return node
    node = node.parentElement
  }
  return container
}

/**
 * 把容器内所有 [attr] 可选项的当前位置快照成内容坐标矩形。
 *
 * 框选开始时调用一次：逐项 getBoundingClientRect（触发一次布局）换算成内容坐标后缓存；
 * 之后滚动 / 命中检测都只用这份快照，不再读布局。
 *
 * @param container - 框选作用容器（在其内 querySelectorAll）
 * @param attr - 可选项标识属性名，如 'data-folder-id'
 * @param scroller - 滚动容器（取 scrollTop / scrollLeft 作换算基准）
 * @param viewportRect - 滚动容器的客户端 rect
 * @returns 各可选项的内容坐标矩形列表
 */
export function snapshotItemContentRects(
  container: HTMLElement,
  attr: string,
  scroller: HTMLElement,
  viewportRect: DOMRect
): ItemContentRect[] {
  const { scrollTop, scrollLeft } = scroller
  const rects: ItemContentRect[] = []
  // 项目 lib.dom 未暴露 querySelectorAll 泛型签名，用基类 Element 即可
  // （自带 getBoundingClientRect / getAttribute）
  container.querySelectorAll(`[${attr}]`).forEach((el) => {
    const id = el.getAttribute(attr)
    if (!id) return
    const r = el.getBoundingClientRect()
    rects.push({
      id,
      left: r.left - viewportRect.left + scrollLeft,
      right: r.right - viewportRect.left + scrollLeft,
      top: r.top - viewportRect.top + scrollTop,
      bottom: r.bottom - viewportRect.top + scrollTop
    })
  })
  return rects
}

/**
 * 选区矩形（内容坐标）与各项做轴向相交，返回命中的 id 列表。
 *
 * @param items - 可选项内容坐标矩形（snapshotItemContentRects 的结果）
 * @param left - 选区左边界（内容坐标，已归一化 min/max）
 * @param right - 选区右边界
 * @param top - 选区上边界
 * @param bottom - 选区下边界
 * @returns 命中项的 id 列表（保持 items 原顺序）
 *
 * @example
 * ```ts
 * computeHitIds(itemRects, 0, 200, 0, 120) // ['1', '2']
 * ```
 */
export function computeHitIds(
  items: ItemContentRect[],
  left: number,
  right: number,
  top: number,
  bottom: number
): string[] {
  return items
    .filter(
      (item) =>
        item.right >= left &&
        item.left <= right &&
        item.bottom >= top &&
        item.top <= bottom
    )
    .map((item) => item.id)
}

/**
 * 边缘自动滚动速度 · 指针越深入边缘区滚得越快（线性，到区外封顶满速）。
 *
 * @param depth - 指针进入边缘区的深度（px，可超过区宽 / 为负，内部按 [0,1] 封顶）
 * @param zone - 边缘区宽度（px）
 * @param maxSpeed - 满速（px / 帧）
 * @returns 本帧滚动步长（px，>= 0；调用方按上 / 下方向决定正负）
 *
 * @example
 * ```ts
 * edgeScrollSpeed(0, 48, 18)   // 0   刚进边缘
 * edgeScrollSpeed(48, 48, 18)  // 18  贴边满速
 * edgeScrollSpeed(80, 48, 18)  // 18  越出视口也封顶
 * ```
 */
export function edgeScrollSpeed(
  depth: number,
  zone: number,
  maxSpeed: number
): number {
  const ratio = Math.max(0, Math.min(depth / zone, 1))
  return maxSpeed * ratio
}

/**
 * 由 Grid 布局参数按 index 算出全量可选项的内容坐标矩形 · 纯函数，不读 DOM。
 *
 * 用于 react-virtualized 虚拟滚动下的框选：视口外的项不在 DOM，但其坐标可由
 * 规整网格几何直接推出（cell 原点 = (col*columnWidth, row*rowHeight)，卡片左上
 * 对齐 cell 左上、右/下留 gap）。坐标系与 Grid 内部滚动内容一致（原点在首格左上）。
 *
 * @param ids - 全量可选项 id（按渲染顺序，不含待归档虚拟项）
 * @param columnCount - 当前列数（AutoSizer 宽 / columnWidth 取整）
 * @param columnWidth - 列槽宽（含 gap）
 * @param rowHeight - 行槽高（含 gap）
 * @param cardWidth - 卡片实际宽
 * @param cardHeight - 卡片实际高
 * @returns 各项内容坐标矩形（保持 ids 顺序）
 *
 * @example
 * ```ts
 * rectsFromGridLayout(['a','b'], 2, 224, 216, 208, 200)
 * // [{id:'a',left:0,right:208,top:0,bottom:200}, {id:'b',left:224,right:432,top:0,bottom:200}]
 * ```
 */
export function rectsFromGridLayout(
  ids: string[],
  columnCount: number,
  columnWidth: number,
  rowHeight: number,
  cardWidth: number,
  cardHeight: number
): ItemContentRect[] {
  const cols = Math.max(1, columnCount)
  const rects: ItemContentRect[] = []
  for (let i = 0; i < ids.length; i++) {
    const row = Math.floor(i / cols)
    const col = i % cols
    const left = col * columnWidth
    const top = row * rowHeight
    rects.push({
      id: ids[i],
      left,
      right: left + cardWidth,
      top,
      bottom: top + cardHeight
    })
  }
  return rects
}
